import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
}

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onUserUpdated: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ open, onClose, user, onUserUpdated }) => {
  const { user: currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const isCurrentUser = currentUser?.id === user?.id;

  // Reset form when modal opens or user changes
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setRole(user.role || '');
      setIsActive(user.isActive);
    }
  }, [user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!user) return;

      const userData = {
        email,
        firstName: firstName || null,
        lastName: lastName || null,
        role: isCurrentUser ? undefined : role,
        isActive: isCurrentUser ? undefined : isActive
      };

      const response = await axios.put(`${API_URL}/api/users/${user.id}`, userData);

      if (response.data.success) {
        setSuccess(true);
        
        // Notify parent component to refresh user list
        onUserUpdated();
        
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        // Special case: If seat limit is reached, show the message from the server
        if (err.response.status === 403 && err.response.data.error === 'Seat limit reached') {
          setError(err.response.data.message || 'Your organization has reached its seat limit.');
        } else {
          setError(err.response.data.error);
        }
      } else {
        setError('Failed to update user');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>User updated successfully</Alert>}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {user && user.email !== email && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.85rem' }}>
              Changing a user's email will send a notification to the new address with a sign-in link.
            </Alert>
          )}
          
          <TextField
            margin="normal"
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          
          <TextField
            margin="normal"
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          
          <FormControl fullWidth margin="normal" disabled={isCurrentUser}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MEMBER">Member</MenuItem>
            </Select>
            {isCurrentUser && (
              <Box sx={{ mt: 1 }}>
                <Chip 
                  size="small" 
                  color="primary" 
                  label="You cannot change your own role" 
                />
              </Box>
            )}
          </FormControl>
          
          <FormControl fullWidth margin="normal" disabled={isCurrentUser}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              value={isActive ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => setIsActive(e.target.value === 'active')}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
            {isCurrentUser && (
              <Box sx={{ mt: 1 }}>
                <Chip 
                  size="small" 
                  color="primary" 
                  label="You cannot change your own status" 
                />
              </Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal; 